import { useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import Icon from 'components/AppIcon';
import { mapLeaveType } from 'utils/common';

const RejectLeaveModal = ({
  isOpen,
  onClose,
  onConfirm,
  leaveDetails,
  type = 'reject', // 'reject' or 'cancel'
  description = '',
  confirmLabel = '',
  showSecondaryButton = true,
  infoMessage = '',
  showRemainOptions = false,
}) => {
  const [reason, setReason] = useState('');

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isReject = type === 'reject';

  const handleSubmit = async (isRemaining = false) => {
    // Validate reason
    if (!reason.trim()) {
      setError('Please provide a reason');
      return;
    }

    if (reason.trim().length < 10) {
      setError('Reason must be at least 10 characters');
      return;
    }

    setIsSubmitting(true);

    try {
      await onConfirm(reason.trim(), isRemaining);
      setReason('');
      setError('');
      setShowConfirmation(false);
      onClose();
    } catch (err) {
      console.log(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setReason('');
    setError('');
    setShowConfirmation(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={handleClose} />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-md bg-white rounded-2xl shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${isReject ? 'bg-red-100' : 'bg-amber-100'}`}>
                <AlertTriangle
                  className={`h-5 w-5 ${isReject ? 'text-red-600' : 'text-amber-600'}`}
                />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                {showConfirmation
                  ? 'Confirm All Remaining Cancellation'
                  : isReject
                    ? 'Reject Leave Request'
                    : 'Cancel Leave Request'}
              </h3>
            </div>
            <button
              onClick={handleClose}
              className="p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {showConfirmation ? (
            <div className="p-6 space-y-4">
              <div className="flex items-start gap-4 p-4 bg-amber-50 rounded-xl border border-amber-200">
                <AlertTriangle className="h-6 w-6 text-amber-600 shrink-0" />
                <div>
                  <h4 className="font-semibold text-amber-900">Are you absolutely sure?</h4>
                  <p className="text-sm text-amber-800 mt-1">
                    This will cancel all your remaining leave days for this request. This action
                    cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
                >
                  Go Back
                </button>
                <button
                  onClick={() => handleSubmit(true)}
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-amber-600 text-white font-medium hover:bg-amber-700 transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? 'Canceling...' : 'Yes, Cancel All'}
                </button>
              </div>
            </div>
          ) : (
            <>
              {description && (
                <div className="mx-5 mt-4 flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 p-3">
                  <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" />
                  <p className="text-sm font-medium text-amber-900">{description}</p>
                </div>
              )}
              {infoMessage && (
                <div className="mx-5 mt-4 flex items-start gap-2.5 rounded-xl border border-blue-200 bg-blue-50 p-3">
                  <Icon name="Info" size={18} className="shrink-0 text-blue-600 mt-0.5" />
                  <p className="text-sm font-medium text-blue-900">{infoMessage}</p>
                </div>
              )}

              {/* Body */}
              <div className="p-5 space-y-4">
                {/* Leave Details (Optional) */}
                {leaveDetails && (
                  <div className="bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200 rounded-xl p-4">
                    <div className="space-y-3">
                      {/* Employee */}
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Icon name="User" className="h-4 w-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Employee</p>
                          <p className="font-medium capitalize text-sm text-gray-900">
                            {leaveDetails?.user?.first_name} {leaveDetails?.user?.last_name}
                          </p>
                        </div>
                      </div>

                      {/* Leave Type */}
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <Icon name="FileText" className="h-4 w-4 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Leave Type</p>
                          <p className="font-medium text-sm text-gray-900">
                            {mapLeaveType(leaveDetails?.type)}
                          </p>
                        </div>
                      </div>

                      {/* Duration */}
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <Icon name="Calendar" className="h-4 w-4 text-green-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Duration</p>
                          <p className="font-medium text-sm text-gray-900">
                            {leaveDetails?.duration}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Reason Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => {
                      setReason(e.target.value);
                      if (error) setError('');
                    }}
                    placeholder={`Please provide a reason for ${isReject ? 'rejecting' : 'canceling'} this leave request...`}
                    rows={4}
                    className={`w-full px-4 py-3 rounded-xl border-2 resize-none transition-all duration-200 focus:outline-none focus:ring-4
                  ${
                    error
                      ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
                      : 'border-gray-200 focus:border-blue-500 focus:ring-blue-100'
                  }`}
                  />
                  {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
                  <p className="mt-1 text-xs text-gray-400">{reason.length}/500 characters</p>
                </div>
              </div>

              {/* Footer */}
              <div className="flex flex-col sm:flex-row gap-3 p-5 border-t border-gray-100">
                {showSecondaryButton && (
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={isSubmitting}
                    className="flex-1 px-4 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                )}

                {showRemainOptions ? (
                  <>
                    <button
                      type="button"
                      onClick={() => handleSubmit(false)}
                      disabled={isSubmitting}
                      className="flex-1 px-4 py-2.5 rounded-xl border-2 border-amber-600 text-amber-600 font-semibold hover:bg-amber-50 transition-colors disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <div className="h-4 w-4 border-2 border-amber-600/30 border-t-amber-600 rounded-full animate-spin" />
                          Processing...
                        </span>
                      ) : (
                        'Cancel Today Only'
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (!reason.trim()) {
                          setError('Please provide a reason');
                          return;
                        }
                        if (reason.trim().length < 10) {
                          setError('Reason must be at least 10 characters');
                          return;
                        }
                        setShowConfirmation(true);
                      }}
                      disabled={isSubmitting}
                      className="flex-1 px-4 py-2.5 rounded-xl bg-amber-600 text-white font-semibold hover:bg-amber-700 transition-colors disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Processing...
                        </span>
                      ) : (
                        'Cancel All Remaining'
                      )}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleSubmit(false)}
                    disabled={isSubmitting}
                    className={`flex-1 px-4 py-2.5 rounded-xl text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                ${isReject ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}`}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Processing...
                      </span>
                    ) : confirmLabel ? (
                      confirmLabel
                    ) : isReject ? (
                      'Reject Request'
                    ) : (
                      'Cancel Request'
                    )}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RejectLeaveModal;
